import { Box, Button, Divider, Typography } from "@mui/material";
import React from "react";
import { CalendarIcon, LocationIcon } from "../../../components/icons";
import { ClockIcon } from "lucide-react";

interface SponsorshipInputCardProps {
  when: string;
  where: string;
  whatTime: string;
  whereBoxRef: React.RefObject<HTMLDivElement | null>;
  timeBoxRef: React.RefObject<HTMLDivElement | null>;

  onWhenClick: (el: HTMLElement) => void;
  onWhereClick: (el: HTMLElement) => void;
  onTimeClick: (el: HTMLElement) => void;
  onApply: () => void;

  formatDate: (date: string) => string;
  formatTime: (time: string) => string;

  buttonText?: string;
  buttonColor?: string;
}

const SponsorshipInputCard: React.FC<SponsorshipInputCardProps> = ({
  when,
  where,
  whatTime,
  whereBoxRef,
  timeBoxRef,
  onWhenClick,
  onWhereClick,
  onTimeClick,
  onApply,
  formatDate,
  formatTime,
  buttonText = "Apply for Sponsorship",
  buttonColor = "#1976d2",
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", md: "800px" },
        mx: "auto",
        borderRadius: { xs: 3, md: 50 },
        overflow: "hidden",
        border: "1px solid #D1D3D7",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
        }}
      >
        {/* WHEN */}
        <Box
          onClick={(e) => onWhenClick(e.currentTarget)}
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3 },
            cursor: "pointer",
            transition: "all 0.3s",
            display: "flex",
            alignItems: "center",
            "&:hover": { bgcolor: "action.hover" },
            WebkitTapHighlightColor: "rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarIcon />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", md: "1rem" },
                  }}
                >
                  WHEN
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: when ? "text.primary" : "text.secondary",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                {formatDate(when)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" }, my: 2 }}
        />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        {/* WHERE */}
        <Box
          ref={whereBoxRef}
          onClick={(e) => onWhereClick(e.currentTarget)}
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3 },
            cursor: "pointer",
            transition: "all 0.3s",
            display: "flex",
            alignItems: "center",
            "&:hover": { bgcolor: "action.hover" },
            WebkitTapHighlightColor: "rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationIcon />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", md: "1rem" },
                  }}
                >
                  WHERE
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: where ? "text.primary" : "text.secondary",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: { xs: "200px", sm: "100%" },
                }}
              >
                {where || "Add location"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" }, my: 2 }}
        />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        {/* WHAT TIME */}
        <Box
          ref={timeBoxRef}
          onClick={(e) => onTimeClick(e.currentTarget)}
          sx={{
            flex: 1,
            p: { xs: 2.5, md: 3 },
            cursor: "pointer",
            transition: "all 0.3s",
            display: "flex",
            alignItems: "center",
            "&:hover": { bgcolor: "action.hover" },
            WebkitTapHighlightColor: "rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ClockIcon />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "0.85rem", md: "1rem" },
                  }}
                >
                  TIME
                </Typography>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: whatTime ? "text.primary" : "text.secondary",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                {formatTime(whatTime)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", md: "block" }, my: 2 }}
        />
        <Divider sx={{ display: { xs: "block", md: "none" } }} />

        {/* APPLY FOR SPONSORSHIP BUTTON */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, md: 2 },
          }}
        >
          <Button
            variant="contained"
            onClick={onApply}
            fullWidth
            sx={{
              background: buttonColor,
              color: "white",
              px: { xs: 3, md: 6 },
              py: { xs: 1.8, md: 2.8 },
              borderRadius: { xs: 2, md: 50 },
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "1rem", md: "1.2rem" },
              whiteSpace: "nowrap",
              boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
              transition: "all 0.3s ease",
              animation: {
                xs: "none",
                md: "breathing 4.5s ease-in-out infinite",
              },
              maxWidth: { md: 300 },
              width: "100%",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
                animation: "none",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
              "@keyframes breathing": {
                "0%": { transform: "scale(1)" },
                "50%": { transform: "scale(1.07)" },
                "100%": { transform: "scale(1)" },
              },
            }}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SponsorshipInputCard;
